import React, { useState } from 'react';

// --- Unit Helpers ---
// Extracts a unit hint from a variable label like "Reduction in Sales Cycle Length (%)" or "Current Average Sales Cycle Length (days)"
const extractUnitFromLabel = (label = '') => {
    const match = label.match(/\(([^)]+)\)/);
    return match ? match[1] : '';
};

// Determines prefix/suffix adornments for an input based on variable metadata or label
const getUnitAdornments = (variable) => {
    const explicitUnit = variable?.unit || '';
    const unit = explicitUnit || extractUnitFromLabel(variable?.label);
    if (!unit) return { prefix: '', suffix: '' };

    const normalized = unit.toLowerCase();
    if (normalized.includes('$') || normalized.includes('usd') || normalized.includes('currency')) {
        return { prefix: '$', suffix: '' };
    }
    if (normalized === 'pp' || normalized.includes('percentage point')) {
        return { prefix: '', suffix: 'pp' };
    }
    if (normalized.includes('%')) {
        return { prefix: '', suffix: '%' };
    }
    // Default to showing the detected unit as a suffix (e.g., days, hours/week)
    return { prefix: '', suffix: unit };
};

// --- Helper Components ---

const Loader = ({ phase }) => (
    <div className="text-center my-8">
        <div className="mx-auto h-16 w-16 animate-spin rounded-full border-4 border-t-4 border-gray-200 border-t-blue-600"></div>
        <p className="mt-4 text-gray-600 font-medium">Executing Pipeline...</p>
        <p className="mt-2 text-sm text-gray-500">{phase}</p>
    </div>
);

const ErrorMessage = ({ message }) => (
    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative my-8" role="alert">
        <strong className="font-bold">Pipeline Error: </strong>
        <span className="block sm:inline">{message}</span>
    </div>
);

const AEView = ({ data, onInputChange, onScenarioChange, currentScenario, roiResult }) => {
    if (!data?.analysis?.roiLevers) return null;

    return (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">AE Calculations Block</h2>
                <div className="flex items-center space-x-2 bg-gray-100 p-1 rounded-lg">
                    <span className="text-sm font-medium px-2">Scenario:</span>
                    {['worst', 'base', 'best'].map(scenario => (
                        <button 
                            key={scenario}
                            onClick={() => onScenarioChange(scenario)}
                            className={`p-2 rounded-md text-sm capitalize ${currentScenario === scenario ? 'bg-blue-600 text-white' : ''}`}
                        >
                            {scenario}
                        </button>
                    ))}
                </div>
            </div>
            <div className="space-y-6">
                {data.analysis.roiLevers.map(lever => (
                    <div key={lever.leverName} className="p-4 border rounded-lg bg-gray-50/50">
                        <h3 className="font-semibold text-lg text-gray-800 mb-4">{lever.leverName}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                             {lever.variables.map(v => {
                                 const { prefix, suffix } = getUnitAdornments(v);
                                 const paddingLeft = prefix ? 'pl-7' : 'pl-3';
                                 const paddingRight = suffix ? 'pr-7' : 'pr-3';
                                 return (
                                     <div key={v.name}>
                                         <label htmlFor={`var-${v.name}`} className="block text-sm font-medium text-gray-700">{v.label}</label>
                                         <div className="mt-1 relative rounded-md shadow-sm">
                                             {prefix && (
                                                 <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                     <span className="text-gray-500 sm:text-sm">{prefix}</span>
                                                 </div>
                                             )}
                                             {suffix && (
                                                 <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                                     <span className="text-gray-500 sm:text-sm">{suffix}</span>
                                                 </div>
                                             )}
                                             <input 
                                                 type="number" 
                                                 id={`var-${v.name}`} 
                                                 value={v.currentValue} 
                                                 onChange={(e) => onInputChange(v.name, parseFloat(e.target.value))}
                                                 step="any"
                                                 className={`roi-input w-full p-2 ${paddingLeft} ${paddingRight} border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500`}
                                             />
                                         </div>
                                         <p className="mt-1 text-gray-500 text-xs">Source: <span className="font-medium">{v.sourceNote}</span></p>
                                     </div>
                                 );
                             })}
                        </div>
                    </div>
                ))}
            </div>
            <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-semibold text-gray-800">Master ROI Formula</h3>
                <code className="block bg-white p-3 rounded-md mt-2 text-sm overflow-x-auto">{data.calculator.formula}</code>
            </div>
            <div className="mt-8 text-center">
                <p className="text-lg text-gray-600 font-medium">Calculated ROI</p>
                <p className="text-5xl font-bold text-blue-600 mt-1">{roiResult.toFixed(2)}%</p>
            </div>
        </div>
    );
};

const CustomerView = ({ story, roiResult, netImpact, paybackPeriod }) => {
    if (!story) return null;
    
    // Simple markdown to HTML
    const createMarkup = () => {
        const formattedStory = story
            .replace(/^### (.*$)/gim, '<h3 class="text-xl font-semibold mt-6 mb-2">$1</h3>')
            .replace(/^## (.*$)/gim, '<h2 class="text-2xl font-bold mt-8 mb-4">$1</h2>')
            .replace(/\*\*(.*)\*\*/g, '<strong>$1</strong>')
            .replace(/\n/g, '<br />');
        return { __html: formattedStory };
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
            <div className="prose max-w-none">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Projected Business Value</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center mb-8">
                    <div className="bg-green-50 p-4 rounded-lg">
                        <p className="text-sm font-medium text-green-800">Projected ROI</p>
                        <p className="text-3xl font-bold text-green-700">{roiResult.toFixed(2)}%</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                        <p className="text-sm font-medium text-green-800">Payback Period (Est.)</p>
                        <p className="text-3xl font-bold text-green-700">~{paybackPeriod} Months</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                        <p className="text-sm font-medium text-green-800">Net Annual Impact</p>
                        <p className="text-3xl font-bold text-green-700">${netImpact.toLocaleString()}</p>
                    </div>
                </div>
                <div dangerouslySetInnerHTML={createMarkup()} />
            </div>
        </div>
    );
};

const ReasoningView = ({ data }) => {
    if (!data?.calculator?.reasoningChain) return null;
    return (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Appendix: Reasoning Chain</h2>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="text-left py-2 px-4 border-b">Input Variable</th>
                            <th className="text-left py-2 px-4 border-b">Value Source</th>
                            <th className="text-left py-2 px-4 border-b">Diagnostic Logic</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.calculator.reasoningChain.map(item => (
                            <tr key={item.variableName} className="hover:bg-gray-50">
                                <td className="py-2 px-4 border-b font-mono text-sm">{item.variableName}</td>
                                <td className="py-2 px-4 border-b text-sm">{item.valueSource}</td>
                                <td className="py-2 px-4 border-b text-sm">{item.diagnosticLogic}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


// --- Main App Component ---

export default function ROI() {
    const [sellerUrl, setSellerUrl] = useState('');
    const [buyerUrl, setBuyerUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [loaderPhase, setLoaderPhase] = useState('');
    const [error, setError] = useState('');
    const [roiData, setRoiData] = useState(null);
    const [activeTab, setActiveTab] = useState('ae-view');
    const [currentScenario, setCurrentScenario] = useState('base');
    
    // --- Calculation Logic ---
    const calculateMetrics = (data) => {
        if (!data?.calculator?.formula || !data?.analysis?.roiLevers) {
            return { roi: 0, netImpact: 0, paybackPeriod: 0 };
        }

        const values = {};
        let totalCost = 0;
        data.analysis.roiLevers.forEach(lever => {
            lever.variables.forEach(v => {
                const value = v.currentValue || 0;
                values[v.name] = value;
                if (v.isCost) {
                    totalCost += value;
                }
            });
        });

        try {
            const argNames = Object.keys(values);
            const argValues = Object.values(values);
            const calculatorFn = new Function(...argNames, `return ${data.calculator.formula}`);
            const roi = calculatorFn(...argValues);

            if (isNaN(roi) || !isFinite(roi)) throw new Error("Calculation resulted in NaN");

            const netImpact = totalCost * (roi / 100);
            const paybackPeriod = (roi > 0) ? (100 / roi).toFixed(1) : 0;
            return { roi, netImpact, paybackPeriod };

        } catch (e) {
            console.error("Calculation error:", e);
            return { roi: 0, netImpact: 0, paybackPeriod: 0 };
        }
    };
    
    const { roi, netImpact, paybackPeriod } = calculateMetrics(roiData);

    // --- Event Handlers ---
    const handlePipelineExecution = async () => {
        if (!sellerUrl || !buyerUrl) {
            setError('Please provide both a Seller and a Buyer URL.');
            return;
        }
        
        setLoading(true);
        setError('');
        setRoiData(null);
        setLoaderPhase('Initiating pipeline...');

        try {
            // *** FIX: Use the full URL to the backend server ***
            // In a real production app, this might be a relative URL if the app is served
            // from the same domain, or an environment variable.
            //const backendUrl = 'http://localhost:4000';
           const backendUrl = 'https://aura-backend-node-mlfv.vercel.app';
            const response = await fetch(`${backendUrl}/api/generate-roi`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sellerUrl, buyerUrl }),
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.message || 'Backend request failed');
            }

            const data = await response.json();
            
            // Initialize 'currentValue' for each variable
            data.analysis.roiLevers.forEach(lever => {
                lever.variables.forEach(v => {
                    v.currentValue = v.baseValue;
                });
            });

            setRoiData(data);
            setCurrentScenario('base');

        } catch (err) {
            // Handle network errors (e.g., backend is not running)
            if (err instanceof TypeError) {
                 setError('Could not connect to the backend server. Is it running?');
            } else {
                 setError(err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleScenarioChange = (scenario) => {
        if (!roiData) return;
        
        const newData = { ...roiData };
        newData.analysis.roiLevers.forEach(lever => {
            lever.variables.forEach(v => {
                v.currentValue = v[`${scenario}Value`];
            });
        });
        
        setRoiData(newData);
        setCurrentScenario(scenario);
    };

    const handleInputChange = (variableName, value) => {
        if (!roiData) return;

        const newData = { ...roiData };
        newData.analysis.roiLevers.forEach(lever => {
            lever.variables.forEach(v => {
                if (v.name === variableName) {
                    v.currentValue = value;
                }
            });
        });

        setRoiData(newData);
        setCurrentScenario('custom'); // Indicate values have been manually changed
    };


    const TABS = {
        'ae-view': 'AE-Friendly Calculations',
        'customer-view': 'Customer-Friendly Value Story',
        'reasoning-view': 'Appendix: Reasoning Chain',
    };

    return (
        <div className="container mx-auto p-4 sm:p-6 lg:p-8 max-w-7xl">
            <header className="text-center mb-8">
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">ROI Calculator</h1>
                <p className="mt-2 text-md sm:text-lg text-gray-600">Powered by AI-driven GTM analysis and symbolic reasoning.</p>
            </header>

            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
                <h2 className="text-xl font-semibold mb-4 text-gray-800">Generate a Tailored ROI Analysis</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                    <div>
                        <label htmlFor="seller-url" className="block text-sm font-medium text-gray-700 mb-2">Seller URL</label>
                        <input type="url" id="seller-url" value={sellerUrl} onChange={e => setSellerUrl(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="e.g., https://www.seller-saas.com" />
                    </div>
                    <div>
                        <label htmlFor="buyer-url" className="block text-sm font-medium text-gray-700 mb-2">Buyer URL</label>
                        <input type="url" id="buyer-url" value={buyerUrl} onChange={e => setBuyerUrl(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="e.g., https://www.potential-customer.com" />
                    </div>
                </div>
                <button onClick={handlePipelineExecution} disabled={loading} className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-transform transform hover:scale-102 flex items-center justify-center disabled:bg-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M12 6V3m0 18v-3M5.636 5.636L3.515 3.515m16.97 16.97l-2.121-2.121M5.636 18.364l-2.121 2.121m16.97-16.97l-2.121 2.121"/></svg>
                    {loading ? 'Executing...' : 'Execute ROI Calculation'}
                </button>
            </div>

            {loading && <Loader phase={loaderPhase} />}
            {error && <ErrorMessage message={error} />}

            {roiData && !loading && (
                <div className="mt-8">
                    <div className="mb-4 border-b border-gray-200">
                        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                            {Object.entries(TABS).map(([tabId, tabName]) => (
                                <button
                                    key={tabId}
                                    onClick={() => setActiveTab(tabId)}
                                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-all ease-in-out ${activeTab === tabId ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                                >
                                    {tabName}
                                </button>
                            ))}
                        </nav>
                    </div>

                    <div>
                        {activeTab === 'ae-view' && <AEView data={roiData} onInputChange={handleInputChange} onScenarioChange={handleScenarioChange} currentScenario={currentScenario} roiResult={roi} />}
                        {activeTab === 'customer-view' && <CustomerView story={roiData.customerStory} roiResult={roi} netImpact={netImpact} paybackPeriod={paybackPeriod} />}
                        {activeTab === 'reasoning-view' && <ReasoningView data={roiData} />}
                    </div>
                </div>
            )}
        </div>
    );
}
